import { useQuery } from '@tanstack/react-query'
import { authService } from '@/services/AuthService'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'

/**
 * A first venue before `firstVenueInputSchema` has accepted it.
 *
 * The zone is still whatever the select holds, including nothing chosen.
 * Parsing the schema is what turns it into a `VenueTimezone`.
 */
export interface FirstVenueDraft {
  name: string
  address: string
  timezone: string
}

/** The time zones a first venue may use, from the server. */
export function useSignupTimezones(): string[] {
  const zones = useQuery({ queryKey: ['signup-timezones'], queryFn: () => authService.signupTimezones(), staleTime: Infinity })
  return zones.data?.timezones ?? []
}

/** The zone a new venue starts on: the browser's own, when the server offers it. */
function browserZone(offered: string[]): string {
  const own = Intl.DateTimeFormat().resolvedOptions().timeZone
  return offered.includes(own) ? own : ''
}

/**
 * A first venue's name, address and time zone — enough to schedule with from the
 * start. The zone list is the server's, so the form cannot offer one the signup
 * would refuse.
 */
export function VenueFields({
  value,
  onChange,
  errors,
  offered,
}: {
  value: FirstVenueDraft
  onChange: (next: FirstVenueDraft) => void
  errors: Partial<Record<keyof FirstVenueDraft, string>>
  /** The zones the server accepts — from `useSignupTimezones`. */
  offered: string[]
}) {
  const timezone = value.timezone === '' ? browserZone(offered) : value.timezone
  return (
    <div className="space-y-3">
      <Field label="Venue name" htmlFor="venue-name" error={errors.name}>
        <Input id="venue-name" value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} />
      </Field>
      <Field label="Venue address" htmlFor="venue-address" error={errors.address}>
        <Input id="venue-address" value={value.address} onChange={(e) => onChange({ ...value, address: e.target.value })} />
      </Field>
      <Field label="Venue time zone" htmlFor="venue-timezone" error={errors.timezone}>
        <Select id="venue-timezone" value={timezone} onChange={(e) => onChange({ ...value, timezone: e.target.value })}>
          <option value="">Choose a time zone…</option>
          {offered.map((z) => (
            <option key={z} value={z}>
              {z.replace(/_/g, ' ')}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  )
}

/**
 * The venue as the picker shows it, including a zone that was only the default.
 *
 * Still a draft: the browser zone may be missing from the server's list, and
 * `''` is not a zone. The caller parses `firstVenueInputSchema` before sending.
 */
export function resolvedVenue(value: FirstVenueDraft, offered: string[]): FirstVenueDraft {
  return value.timezone === '' ? { ...value, timezone: browserZone(offered) } : value
}
